export interface PartialTestProgress {
  type: string;
  mode: string;
  selectedSection: string;
  fullExamStage: "reading" | "listening" | "writing";
  timeLeft: number;
  currentQuestionIdx: number;
  selectedAnswers: Record<number, string>;
  reviewMarked: Record<number, boolean>;
  writingText: string;
  fullExamStartedAt: number;
  readingResults: any;
  listeningResults: any;
}

class TestStateService {
  private inProgress: boolean = false;
  private finishing: boolean = false;
  private listeners: Set<(state: boolean) => void> = new Set();

  constructor() {
    this.inProgress = sessionStorage.getItem("tef_test_in_progress") === "true";
  }

  get isTestInProgress(): boolean {
    return this.inProgress;
  }

  /** True while the user is intentionally completing the test via the Finish button. */
  get isFinishingTest(): boolean {
    return this.finishing;
  }

  setTestInProgress(value: boolean) {
    this.inProgress = value;
    // Do NOT clear `finishing` here — it must stay true until React Router
    // completes the navigation triggered by the Finish button. The flag is
    // cleared automatically when setIsFinishingTest(false) is called or when
    // a new test session starts.
    sessionStorage.setItem("tef_test_in_progress", value ? "true" : "false");
    this.listeners.forEach(listener => listener(value));
  }

  /** Call this BEFORE navigating away via the official Finish button so the blocker is skipped. */
  setIsFinishingTest(value: boolean) {
    this.finishing = value;
  }

  subscribe(listener: (state: boolean) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  savePartialProgress(data: PartialTestProgress) {
    sessionStorage.setItem("tef_partial_test_progress", JSON.stringify(data));
  }

  getPartialProgress(): PartialTestProgress | null {
    const data = sessionStorage.getItem("tef_partial_test_progress");
    try {
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error("Failed to parse partial progress:", e);
      return null;
    }
  }

  clearPartialProgress() {
    sessionStorage.removeItem("tef_partial_test_progress");
  }
}

export const testStateService = new TestStateService();
export default testStateService;
